<template>
  <div class="weight-input">
    <div
      v-if="outerLabelValue"
      :id="labelId"
      class="outer-label"
      :class="{ 'outer-label--bound': !fields.outerLabelFullWidth }"
    >
      <TipTap v-model="outerLabelValue" mode="single" />
    </div>
    <BaseInputShell v-bind="shellProps" :error="showError">
      <BaseInputField
        :id="inputId"
        name="weight"
        inputmode="numeric"
        autocomplete="one-time-code"
        :modelValue="fieldDisplayValue"
        @update:modelValue="onInput"
        @blur="handleBlur"
        @submit="handleSubmit"
        v-model:inner-label-data="innerLabelData"
        :inner-label-fallback="innerLabelFallback"
        :placeholder="placeholderText"
        :autofocus="fields.autoFocus"
        :aria-labelledby="outerLabelValue ? labelId : undefined"
        :aria-describedby="describedBy(showValidationMessage && errorId)"
        :aria-invalid="showError"
        v-bind="fieldProps"
      />
    </BaseInputShell>
    <div v-if="showStandardMessage" :id="errorId" class="validation-message">
      <TipTap v-model="validationMessageValue" mode="single" />
    </div>
    <div v-else-if="showSuggestedMessage" :id="errorId" class="validation-message">
      <TipTap v-model="suggestedRangeMessageValue" mode="single" />
    </div>
    <div
      v-if="displayValue && fields.showSuccessMessage && !showError && successMessageValue"
      class="success-message"
    >
      <TipTap v-model="successMessageValue" mode="single" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, useId } from 'vue'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useLocale } from '@render/composables/useLocale'
import { useFormInput } from '@render/composables/useFormInput'
import { useInputAria } from '@render/composables/useInputAria'
import { useInputValidation } from '@render/composables/useInputValidation'
import { usePrefillFromVariable } from '@render/composables/usePrefillFromVariable'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { kgToLbs, lbsToKg, roundForDisplay } from '@render/utils/unitConversion'
import { tiptapToText } from '@render/lib/tiptapToText'
import { parseNumericVar } from '@render/lib/parseNumericVar'
import BaseInputShell from '../BaseInput/BaseInputShell.vue'
import BaseInputField from '../BaseInput/BaseInputField.vue'
import TipTap from '@render/lib/TipTap'
import type { WeightInputFields } from './definition'

const emit = defineEmits<{
  (e: 'onSubmit'): void
}>()

const fields = defineModel<WeightInputFields>('fields', {
  required: true,
  default: () => ({
    valueVariable: undefined,
    prefillVariable: undefined,
    measurementVariable: undefined,
    isValidVariable: undefined,
    outerLabel: undefined,
    placeholder: undefined,
    outerLabelFullWidth: false,
    outerLabelColor: undefined,
    outerLabelFontSize: undefined,
    outerLabelLineHeight: undefined,
    outerLabelFontWeight: undefined,
    outerLabelTextAlign: undefined,
    outerLabelMarginBottom: undefined,
    metricInnerLabel: undefined,
    imperialInnerLabel: undefined,
    validationMessage: undefined,
    validateSuggestedWeight: false,
    suggestedRangeValidationMessage: undefined,
    successMessage: undefined,
    showSuccessMessage: false,
    required: undefined,
    min: undefined,
    max: undefined,
    bgColor: undefined,
    bgColorHover: undefined,
    bgColorActive: undefined,
    bgColorError: undefined,
    bgColorDisabled: undefined,
    borderColor: undefined,
    borderColorHover: undefined,
    focusBorderColor: undefined,
    borderColorError: undefined,
    borderColorDisabled: undefined,
    borderRadius: undefined,
    textColor: undefined,
    textColorPlaceholder: undefined,
    textColorDisabled: undefined,
    labelColor: undefined,
    labelColorDisabled: undefined,
    labelFontSize: undefined,
    labelLineHeight: undefined,
    labelFontWeight: undefined,
    numbersModeFontSize: undefined,
    numbersModeLineHeight: undefined,
    numbersModeFontWeight: undefined,
    minHeight: undefined,
    maxWidth: undefined,
    padding: undefined,
  }),
})

const pipe = usePipe()
const loc = useLocale()
const inputId = `${useId()}-weight`

// Explicit metric/imperial var wins; otherwise fall back to the locale default.
const isImperial = computed(() => {
  const raw = pipe.getData(`var.${fields.value.measurementVariable?.key ?? ''}`)
  if (raw === 'imperial') return true
  if (raw === 'metric') return false
  return loc.defaultMeasurementSystem.value === 'imperial'
})

// We use metric for data as a source of truth.
const kg = computed(() =>
  parseNumericVar(pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`)),
)

// UI value:
// Convert metric to imperial or round if needed. Empty when no value.
const displayValue = computed<number | string>(() => {
  if (kg.value === undefined) return ''
  return isImperial.value ? kgToLbs(kg.value) : roundForDisplay(kg.value)
})

// Editor-only: mirror the interpolation "?" shown for an unresolved value.
// A prefill-configured field stays empty in the editor preview (integrations don't run
// there, so the source is never computed) - show "?" instead of blank so authors know a
// value lands here on the live build. Gated to EDITOR, so the built pipe never shows it.
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)
const fieldDisplayValue = computed<number | string>(() => {
  if (kg.value !== undefined) return displayValue.value
  return isEditor.value && fields.value.prefillVariable?.key ? '?' : ''
})

const placeholderText = computed(() =>
  tiptapToText(pipe.getTranslation(fields.value.placeholder?.key)),
)

// Translatable inner label for units
const innerLabelData = computed({
  get: () => {
    const key = isImperial.value
      ? fields.value.imperialInnerLabel?.key
      : fields.value.metricInnerLabel?.key

    return pipe.getTranslation(key)
  },
  set: (val: object) => {
    const key = isImperial.value
      ? fields.value.imperialInnerLabel?.key
      : fields.value.metricInnerLabel?.key

    if (key) pipe.setTranslation(key, val)
  },
})
const innerLabelFallback = computed(() =>
  isImperial.value ? loc.config.value.weight.pound.short : loc.config.value.weight.kilogram.short,
)

// Validation runs against rounded value to match integer UI value and integer min/max from admin
const validation = useInputValidation(
  () => (kg.value === undefined ? undefined : Math.round(kg.value)),
  () => ({
    required: fields.value.required,
    min: fields.value.min,
    max: fields.value.max,
  }),
)

// Healthy-range lower bound (kg) derived from the user's height by the fitness-functions
// integration. Enforced only when its global variable persists.
const suggestedMinKg = computed(() =>
  parseNumericVar(pipe.getData('var.global.minSuggestedWeightKg')),
)

// True when the value is at/above the suggested minimum (or the check is off / no bound persists).
// Opt-in per instance via `validateSuggestedWeight` — a preset minSuggestedWeight must not block
// inputs whose author didn't enable this. Compared against the rounded kg value, matching the
// integer bound the integration writes.
const suggestedRangeValid = computed(() => {
  if (!fields.value.validateSuggestedWeight) return true
  if (kg.value === undefined) return true
  const v = Math.round(kg.value)
  if (suggestedMinKg.value !== undefined && v < suggestedMinKg.value) return false
  return true
})

const isValid = computed(() => validation.isValid.value && suggestedRangeValid.value)

const formInput = useFormInput({
  validate: () => isValid.value,
  disableRealtimeValidation: () => fields.value.disableRealtimeValidation === true,
  persistKeys: () => [fields.value.valueVariable?.key, fields.value.isValidVariable?.key],
})

// Standard (required/min/max) errors stay touch-gated — don't nag before the
// user has interacted with the field.
const showStandardError = computed(() => formInput.showErrors.value && !validation.isValid.value)

// Suggested-range errors surface as soon as the value is concretely out of range
// (a real number below the suggested minimum). Unlike "required", there's nothing
// to wait for — the CTA is already blocked via isValidVariable, so the customer
// must see why immediately. Standard validation takes precedence. We still honor
// `disableRealtimeValidation` (errors only after a submit attempt) when set.
const showSuggestedError = computed(() => {
  if (!validation.isValid.value || suggestedRangeValid.value) return false
  return fields.value.disableRealtimeValidation ? formInput.showErrors.value : true
})

const showError = computed(() => showStandardError.value || showSuggestedError.value)
// The author's label and validation copy sit OUTSIDE the <label> shell, so they
// are associated by id rather than by wrapping — see `useInputAria`.
const { labelId, errorId, describedBy } = useInputAria()
// Only one of the two message rows is ever in the DOM (v-if / v-else-if), so
// they share one id and `aria-describedby` points at whichever is showing.
const showStandardMessage = computed(
  () => showStandardError.value && !!validationMessageValue.value,
)
const showSuggestedMessage = computed(
  () =>
    !showStandardMessage.value && showSuggestedError.value && !!suggestedRangeMessageValue.value,
)
const showValidationMessage = computed(
  () => showStandardMessage.value || showSuggestedMessage.value,
)

function handleBlur() {
  validation.onBlur()
  if (kg.value !== undefined) formInput.handleBlur()
}

watch(
  isValid,
  (valid) => {
    if (fields.value.isValidVariable?.key) {
      // Transient validity flag — reactive for CTA state, persisted on submit.
      pipe.setData(`var.${fields.value.isValidVariable.key}`, valid, { persist: false })
    }
  },
  { immediate: true },
)

const outerLabelValue = computed({
  get: () => pipe.getTranslation(fields.value.outerLabel?.key),
  set: (val: object) => {
    const key = fields.value.outerLabel?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const validationMessageValue = computed({
  get: () => pipe.getTranslation(fields.value.validationMessage?.key),
  set: (val: object) => {
    const key = fields.value.validationMessage?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const suggestedRangeMessageValue = computed({
  get: () => pipe.getTranslation(fields.value.suggestedRangeValidationMessage?.key),
  set: (val: object) => {
    const key = fields.value.suggestedRangeValidationMessage?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const successMessageValue = computed({
  get: () => pipe.getTranslation(fields.value.successMessage?.key),
  set: (val: object) => {
    const key = fields.value.successMessage?.key
    if (key) pipe.setTranslation(key, val)
  },
})

function setValueVariable(value: number | null) {
  if (fields.value.valueVariable?.key) {
    // Transient: live in the reactive store, persisted on form submit.
    pipe.setData(`var.${fields.value.valueVariable.key}`, value, { persist: false })
  }
}

// If imperial - convert to metric and write back to store. Empty input clears the variable.
function onInput(val: number | string) {
  if (val === '') {
    setValueVariable(null)
    return
  }
  const num = Number(val)
  setValueVariable(isImperial.value ? lbsToKg(num) : num)
}

// Pre-fill from another (kg) variable when the field is empty, e.g. the goal-weight
// input seeded from global.suggestedTargetWeightKg. The source is already in kg, so no
// conversion is needed; the imperial display derives from the seeded kg on its own.
usePrefillFromVariable({
  sourceKey: () => fields.value.prefillVariable?.key,
  hasValue: () => kg.value !== undefined,
  seed: (value) => setValueVariable(value),
})

async function handleSubmit() {
  const { targetInvoked } = await formInput.requestSubmit()
  if (targetInvoked) return
  if (!validation.isValid.value) {
    validation.markBlurred()
    formInput.markTouched()
    return
  }
  emit('onSubmit')
}

useComponentScopedVariables('WeightInput', () => ({
  min: fields.value.min,
  max: fields.value.max,
  value: kg.value,
}))

const overrideBgColor = computed(() => getColorStyleData(fields.value.bgColor))
const overrideBgColorHover = computed(() => getColorStyleData(fields.value.bgColorHover))
const overrideBgColorActive = computed(() => getColorStyleData(fields.value.bgColorActive))
const overrideBgColorError = computed(() => getColorStyleData(fields.value.bgColorError))
const overrideBgColorDisabled = computed(() => getColorStyleData(fields.value.bgColorDisabled))
const overrideBorderColor = computed(() => getColorStyleData(fields.value.borderColor))
const overrideBorderColorHover = computed(() => getColorStyleData(fields.value.borderColorHover))
const overrideFocusBorderColor = computed(() => getColorStyleData(fields.value.focusBorderColor))
const overrideBorderColorError = computed(() => getColorStyleData(fields.value.borderColorError))
const overrideBorderColorDisabled = computed(() =>
  getColorStyleData(fields.value.borderColorDisabled),
)
const overrideBorderRadius = computed(() => getSizeStyleData(fields.value.borderRadius))
const overrideTextColor = computed(() => getColorStyleData(fields.value.textColor))
const overrideTextColorPlaceholder = computed(() =>
  getColorStyleData(fields.value.textColorPlaceholder),
)
const overrideTextColorDisabled = computed(() => getColorStyleData(fields.value.textColorDisabled))
const overrideLabelColor = computed(() => getColorStyleData(fields.value.labelColor))
const overrideLabelColorDisabled = computed(() =>
  getColorStyleData(fields.value.labelColorDisabled),
)
const overrideLabelFontSize = computed(() => getSizeStyleData(fields.value.labelFontSize))
const overrideLabelLineHeight = computed(() => getSizeStyleData(fields.value.labelLineHeight))
const overrideNumbersModeFontSize = computed(() =>
  getSizeStyleData(fields.value.numbersModeFontSize),
)
const overrideNumbersModeLineHeight = computed(() =>
  getSizeStyleData(fields.value.numbersModeLineHeight),
)
const overrideOuterLabelColor = computed(
  () => getColorStyleData(fields.value.outerLabelColor) ?? 'var(--color-text-secondary)',
)
const overrideOuterLabelFontSize = computed(
  () => getSizeStyleData(fields.value.outerLabelFontSize) ?? 'var(--text-body-sm-size)',
)
const overrideOuterLabelLineHeight = computed(
  () => getSizeStyleData(fields.value.outerLabelLineHeight) ?? 'var(--text-body-sm-line-height)',
)
const overrideOuterLabelFontWeight = computed(
  () => fields.value.outerLabelFontWeight ?? 'var(--text-body-sm-weight)',
)
const overrideOuterLabelTextAlign = computed(() => fields.value.outerLabelTextAlign ?? 'left')
const overrideOuterLabelMarginBottom = computed(
  () => getSizeStyleData(fields.value.outerLabelMarginBottom) ?? 'var(--space-8)',
)
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight))
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth) ?? '100%')
const overridePadding = computed(() => getSpacingStyleData(fields.value.padding))

const shellProps = computed(() => ({
  bgColor: overrideBgColor.value,
  bgColorHover: overrideBgColorHover.value,
  bgColorActive: overrideBgColorActive.value,
  bgColorError: overrideBgColorError.value,
  bgColorDisabled: overrideBgColorDisabled.value,
  borderColor: overrideBorderColor.value,
  borderColorHover: overrideBorderColorHover.value,
  focusBorderColor: overrideFocusBorderColor.value,
  borderColorError: overrideBorderColorError.value,
  borderColorDisabled: overrideBorderColorDisabled.value,
  borderRadius: overrideBorderRadius.value,
  minHeight: overrideMinHeight.value,
  maxWidth: overrideMaxWidth.value,
  padding: overridePadding.value,
}))

const fieldProps = computed(() => ({
  textColor: overrideTextColor.value,
  textColorPlaceholder: overrideTextColorPlaceholder.value,
  textColorDisabled: overrideTextColorDisabled.value,
  labelColor: overrideLabelColor.value,
  labelColorDisabled: overrideLabelColorDisabled.value,
  labelFontSize: overrideLabelFontSize.value,
  labelLineHeight: overrideLabelLineHeight.value,
  labelFontWeight: fields.value.labelFontWeight,
  numbersModeFontSize: overrideNumbersModeFontSize.value,
  numbersModeLineHeight: overrideNumbersModeLineHeight.value,
  numbersModeFontWeight: fields.value.numbersModeFontWeight,
}))
</script>

<style scoped lang="scss">
.weight-input {
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.outer-label {
  margin-bottom: v-bind(overrideOuterLabelMarginBottom);
  color: v-bind(overrideOuterLabelColor);
  font-size: v-bind(overrideOuterLabelFontSize);
  font-weight: v-bind(overrideOuterLabelFontWeight);
  line-height: v-bind(overrideOuterLabelLineHeight);
  text-align: v-bind(overrideOuterLabelTextAlign);
}

.outer-label--bound {
  max-width: v-bind(overrideMaxWidth);
  margin-left: auto;
  margin-right: auto;
}

.validation-message {
  margin-top: var(--space-4);
  color: var(--color-feedback-error-text);
  font-size: var(--text-body-sm-size);
  line-height: var(--text-body-sm-line-height);
  text-align: center;
}

.success-message {
  margin-top: var(--space-4);
  color: var(--color-feedback-success-text);
  font-size: var(--text-body-sm-size);
  line-height: var(--text-body-sm-line-height);
  text-align: center;
}
</style>
